import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import {
  PERSONA_ROLES,
  type CreatePersonaInput,
  type MarketPersona,
  type PersonaRole,
} from '@ispot/shared'

const ROLE_LABELS: Record<PersonaRole, string> = {
  economic_buyer: 'Economic buyer',
  technical_buyer: 'Technical buyer',
  user: 'End user',
  influencer: 'Influencer',
}

interface Props {
  persona: MarketPersona | null // null = create mode
  onCancel: () => void
  onSave: (input: CreatePersonaInput) => Promise<void>
}

export function PersonaDialog({ persona, onCancel, onSave }: Props) {
  const [name, setName] = useState(persona?.name ?? '')
  const [role, setRole] = useState<PersonaRole>(persona?.role ?? 'economic_buyer')
  const [description, setDescription] = useState(persona?.description ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setName(persona?.name ?? '')
    setRole(persona?.role ?? 'economic_buyer')
    setDescription(persona?.description ?? '')
  }, [persona])

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Name is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave({
        name: trimmed,
        role,
        description: description.trim() || null,
        linkedWorkshopPersonaId: persona?.linkedWorkshopPersonaId ?? null,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onCancel}
    >
      <div
        className="w-[480px] rounded-lg border p-5 shadow-lg"
        style={{
          backgroundColor: 'var(--color-bg, white)',
          borderColor: 'var(--color-border-subtle)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3
            className="font-display text-base font-semibold"
            style={{ color: 'var(--color-text)' }}
          >
            {persona ? 'Edit persona' : 'New persona'}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 transition-colors hover:bg-[var(--color-warm-100)]"
            aria-label="Close"
          >
            <X className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="e.g. VP of Engineering"
              className="w-full rounded-md border px-3 py-2 text-sm outline-none"
              style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-warm-50)',
                color: 'var(--color-text)',
              }}
            />
          </Field>

          <Field label="Role">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as PersonaRole)}
              className="w-full rounded-md border px-3 py-2 text-sm outline-none"
              style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-warm-50)',
                color: 'var(--color-text)',
              }}
            >
              {PERSONA_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Goals, pains, buying context…"
              className="w-full rounded-md border px-3 py-2 text-sm outline-none"
              style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-warm-50)',
                color: 'var(--color-text)',
              }}
            />
          </Field>
        </div>

        {error ? (
          <p className="mt-3 text-xs" style={{ color: '#b14132' }}>
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-3 py-1.5 text-xs font-medium"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="rounded-md px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-business, #0984e3)' }}
          >
            {saving ? 'Saving…' : persona ? 'Save changes' : 'Create persona'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span
        className="mb-1 block text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {label}
      </span>
      {children}
    </label>
  )
}

export { ROLE_LABELS }
